import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search, Filter, Download, Eye, UserPlus,
  MoreVertical, RefreshCw, Mail, Phone, MapPin,
  ShieldCheck, ShieldX, Star,
} from 'lucide-react';
import { Card } from '@/components/ui';
import { api } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

const mockUsers = [
  { id: 1, name: 'أحمد محمد علي', email: 'ahmed@example.com', phone: '01012345678', address: 'القاهرة، مصر الجديدة', orders_count: 45, total_spent: 4500, rating: 4.8, status: 'active', created_at: '2025-08-15' },
  { id: 2, name: 'سارة علي حسن', email: 'sara@example.com', phone: '01098765432', address: 'الإسكندرية، سموحة', orders_count: 32, total_spent: 2800, rating: 4.5, status: 'active', created_at: '2025-09-20' },
  { id: 3, name: 'محمود حسن إبراهيم', email: 'mahmoud@example.com', phone: '01155544433', address: 'الجيزة، المهندسين', orders_count: 18, total_spent: 1600, rating: 4.2, status: 'active', created_at: '2025-10-01' },
  { id: 4, name: 'فاطمة أحمد محمد', email: 'fatma@example.com', phone: '01234567890', address: 'القاهرة، المعادي', orders_count: 67, total_spent: 7800, rating: 4.9, status: 'active', created_at: '2025-06-10' },
  { id: 5, name: 'يوسف عبدالله سعيد', email: 'yousef@example.com', phone: '01567890123', address: 'المنصورة', orders_count: 5, total_spent: 350, rating: 3.5, status: 'suspended', created_at: '2026-01-05' },
  { id: 6, name: 'نورا خالد عمر', email: 'noura@example.com', phone: '01345678901', address: 'القاهرة، التجمع الخامس', orders_count: 28, total_spent: 3200, rating: 4.7, status: 'active', created_at: '2025-11-18' },
];

export default function UsersPage() {
  const { t, formatCurrency, language } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const { data: usersData, isLoading, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.getUsers({ page: 1 }),
  });

  const users = usersData?.data || mockUsers;

  const filteredUsers = users.filter((user: any) => {
    const matchSearch = !searchQuery ||
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.phone?.includes(searchQuery);
    const matchStatus = statusFilter === 'all' || user.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">{t('users')}</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {language === 'ar' ? `إجمالي ${users.length} مستخدم` : `Total ${users.length} users`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <RefreshCw size={18} />
          </button>
          <button className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
            <Download size={18} />
            <span className="hidden sm:inline">{t('export')}</span>
          </button>
          <button className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-medium transition-colors shadow-lg shadow-primary-500/25">
            <UserPlus size={18} />
            <span className="hidden sm:inline">{t('add')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <ShieldCheck className="text-blue-600 dark:text-blue-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('active')}</p>
              <p className="text-2xl font-bold text-slate-800 dark:text-white">{users.filter((u: any) => u.status === 'active').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <ShieldX className="text-red-600 dark:text-red-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('suspended')}</p>
              <p className="text-2xl font-bold text-slate-800 dark:text-white">{users.filter((u: any) => u.status === 'suspended').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Star className="text-amber-600 dark:text-amber-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{language === 'ar' ? 'متوسط التقييم' : 'Avg Rating'}</p>
              <p className="text-2xl font-bold text-slate-800 dark:text-white">4.6</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <Card>
        <div className="p-4 flex flex-col sm:flex-row items-center gap-4">
          <div className="relative flex-1 w-full">
            <Search className="absolute top-1/2 -translate-y-1/2 start-4 text-slate-400" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('search')}
              className="w-full ps-12 pe-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">{t('all')}</option>
            <option value="active">{t('active')}</option>
            <option value="suspended">{t('suspended')}</option>
          </select>
          <button className="flex items-center gap-2 px-5 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <Filter size={18} />
            {t('filter')}
          </button>
        </div>
      </Card>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-100 dark:border-slate-700 animate-pulse">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-slate-200 dark:bg-slate-700" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4" />
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))
        ) : filteredUsers.length === 0 ? (
          <div className="col-span-full p-12 text-center">
            <div className="text-slate-400 dark:text-slate-500">
              <Search size={48} className="mx-auto mb-3 opacity-50" />
              <p className="font-medium">{t('noData')}</p>
            </div>
          </div>
        ) : (
          filteredUsers.map((user: any) => (
            <div key={user.id} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-slate-900/50 transition-all group">
              <div className="p-6">
                {/* User Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-xl font-bold">
                      {user.name?.[0]}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800 dark:text-white">{user.name}</h3>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold mt-1 ${
                        user.status === 'active' 
                          ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' 
                          : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {user.status === 'active' ? <ShieldCheck size={12} /> : <ShieldX size={12} />}
                        {t(user.status)}
                      </span>
                    </div>
                  </div>
                  <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 opacity-0 group-hover:opacity-100 transition-all">
                    <MoreVertical size={18} />
                  </button>
                </div>

                {/* User Info */}
                <div className="space-y-2.5 mb-4">
                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                    <Mail size={15} />
                    <span>{user.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                    <Phone size={15} />
                    <span dir="ltr">{user.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                    <MapPin size={15} />
                    <span>{user.address}</span>
                  </div>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-3 pt-4 border-t border-slate-100 dark:border-slate-700">
                  <div className="text-center">
                    <p className="text-lg font-bold text-slate-800 dark:text-white">{user.orders_count}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('orders')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-slate-800 dark:text-white">{formatCurrency(user.total_spent)}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{language === 'ar' ? 'إنفاق' : 'Spent'}</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Star size={14} className="text-amber-400 fill-amber-400" />
                      <span className="text-lg font-bold text-slate-800 dark:text-white">{user.rating}</span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('rating')}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center border-t border-slate-100 dark:border-slate-700">
                <button className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                  <Eye size={16} />
                  {t('view')}
                </button>
                <div className="w-px h-8 bg-slate-100 dark:bg-slate-700" />
                <button className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                  <Mail size={16} />
                  {language === 'ar' ? 'رسالة' : 'Message'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
